import axios from 'axios';
import { apiClient } from './api/apiClient';
import { API_ENDPOINTS } from '../config/api';

/**
 * 조명 시뮬레이터 — manage-backend(/v0.1/lights-simulator/*) 프록시를 통해
 * 현장 조명 서버(lights)의 시뮬레이션 엔드포인트를 호출한다.
 *
 * 두 가지 역할:
 * 1. 도달 체크(DIMMING) — 조명 on/off 요청이 lights 서비스까지 도달하는지 검증.
 * 2. Dry-run 캡처(MODE/EVENTS/MOTION/EMERGENCY/CONTROL) — fan-out 결과를
 *    캡처해 맵에 하이라이트 + 이벤트 패널로 시각화.
 */

/** lights 의 dimmingControl 한 건 — 디바이스로 전송될 '예정' 프레임. */
export interface LightDimmingFrame {
  modId: number;
  mode: 'normal' | 'blink' | 'fade';
  value: number;
  value2?: number;
  duration?: number;
  durCount?: number;
}

/** lights 시뮬레이션 ack. 도달 + 검증 통과의 증거. */
export interface LightsSimulationAck {
  result: string;
  simulation: boolean;
  cctvName: string;
  frames: Array<LightDimmingFrame & { modeInt: number }>;
  receivedAt: string;
  message: string;
}

/**
 * on/off 요청을 lights dimming 프레임으로 변환한다.
 * on = value 100(전점등), off = value 0(소등), mode 는 normal 고정.
 * modId 기본 127 — 도면상 그룹/루트 모듈 관례(lights dimming-multi 예시값).
 */
export function buildOnOffFrames(on: boolean, modId: number): LightDimmingFrame[] {
  return [{ modId, mode: 'normal', value: on ? 100 : 0 }];
}

/**
 * dry-run 캡처 이벤트 한 건 — lights 가 수행할 뻔한 조명 제어 명령.
 * seq: 단조 증가 cursor. ts: lights 측 타임스탬프.
 */
export interface SimEvent {
  seq: number;
  ts: string;
  cctvName: string;
  section: string;
  lightType: string;
  modIds: number[];
  mode: string;
  value: number;
  on: boolean;
}

/** getEvents 응답 */
export interface SimEventsResponse {
  enabled: boolean;
  cursor: number;
  events: SimEvent[];
}

/** setMode 응답 */
export interface SimModeResponse {
  enabled: boolean;
}

/**
 * 직접 제어 응답. lights 가 "OK" 만 주던 것을 정직하게 확장 —
 * captured 로 "요청은 받았지만 캡처된 조명 명령 0건"(모듈 매핑 없음 등)을 구분한다.
 * captured: dry-run 일 때만 측정(아니면 null). errors: dimmer 호출 에러 목록.
 */
export interface ControlResult {
  requested: number;
  captured: number | null;
  errors: string[];
}

/** 프록시 실패/에러를 풀어서 Error 로 변환하는 공통 헬퍼. */
function unwrapAxiosError(e: unknown): never {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { message?: string; error?: string } | undefined;
    const serverMsg = data?.message || data?.error;
    if (serverMsg) throw new Error(serverMsg);
  }
  throw e;
}

/** envelope {success:false, message} 패턴 체크. */
function checkEnvelope(data: { success?: boolean; message?: string }): void {
  if (data.success === false) {
    throw new Error(data.message || '조명 서버 오류');
  }
}

export class LightsSimulatorService {
  /** 조명 on/off 도달 체크. 성공 시 lights 의 ack 를 그대로 반환. */
  static async simulateDimming(
    cctvName: string,
    frames: LightDimmingFrame[],
  ): Promise<LightsSimulationAck> {
    try {
      const res = await apiClient.post<
        LightsSimulationAck & { success?: boolean; message?: string }
      >(API_ENDPOINTS.LIGHTS_SIMULATOR.DIMMING, { cctvName, frames });
      checkEnvelope(res.data);
      return res.data;
    } catch (e) {
      unwrapAxiosError(e);
    }
  }

  /**
   * dry-run 모드를 ON/OFF 한다.
   * 403 → lights 서비스 측에서 시뮬레이션 비허용(LIGHTS_SIMULATION_ALLOW=false).
   */
  static async setMode(enabled: boolean): Promise<SimModeResponse> {
    try {
      const res = await apiClient.post<SimModeResponse & { success?: boolean; message?: string }>(
        API_ENDPOINTS.LIGHTS_SIMULATOR.MODE,
        { enabled },
      );
      checkEnvelope(res.data);
      return { enabled: res.data.enabled };
    } catch (e) {
      unwrapAxiosError(e);
    }
  }

  /**
   * since 커서 이후의 캡처 이벤트를 가져온다.
   * 최초 호출: since=0. 이후 cursor 를 유지해 새 이벤트만 수신.
   */
  static async getEvents(since: number): Promise<SimEventsResponse> {
    try {
      const res = await apiClient.get<SimEventsResponse & { success?: boolean; message?: string }>(
        `${API_ENDPOINTS.LIGHTS_SIMULATOR.EVENTS}?since=${since}`,
      );
      checkEnvelope(res.data);
      return {
        enabled: res.data.enabled,
        cursor: res.data.cursor,
        events: res.data.events ?? [],
      };
    } catch (e) {
      unwrapAxiosError(e);
    }
  }

  /**
   * 모션 감지를 트리거한다 — fan-out이 실제로 실행되며 dry-run ON이면 캡처됨.
   */
  static async triggerMotion(
    cctvName: string,
    direction: 'left' | 'right',
  ): Promise<void> {
    try {
      const res = await apiClient.post<{ success?: boolean; message?: string }>(
        API_ENDPOINTS.LIGHTS_SIMULATOR.MOTION,
        { cctvName, direction },
      );
      checkEnvelope(res.data);
    } catch (e) {
      unwrapAxiosError(e);
    }
  }

  /**
   * 비상 호출을 트리거한다.
   * state='alert' → 비상, state='release' → 해제.
   */
  static async triggerEmergency(
    cctvName: string,
    state: 'alert' | 'release',
  ): Promise<void> {
    try {
      const res = await apiClient.post<{ success?: boolean; message?: string }>(
        API_ENDPOINTS.LIGHTS_SIMULATOR.EMERGENCY,
        { cctvName, state },
      );
      checkEnvelope(res.data);
    } catch (e) {
      unwrapAxiosError(e);
    }
  }

  /**
   * 비상벨 호출을 하드웨어 입력처럼 모사한다(lights SOS 경로).
   * 벨→CCTV 해석(emergency_bells)과 SOS 점멸은 조명제어서버가 실제 코드로 수행하므로,
   * 실제 점멸 대상 CCTV 는 dry-run 캡처 이벤트로 확인한다.
   * cctvName: 벨 신호를 수신한 것으로 가정할 CCTV(벨의 linked CCTV).
   * customId: 벨 커스텀 식별자(예: "0x4100000000000000") — 없으면 서버가 유도.
   */
  static async triggerBellCall(
    cctvName: string,
    state: 'alert' | 'release',
    customId?: string,
  ): Promise<void> {
    try {
      const res = await apiClient.post<{ success?: boolean; message?: string }>(
        API_ENDPOINTS.LIGHTS_SIMULATOR.BELL,
        { cctvName, state, customId },
      );
      checkEnvelope(res.data);
    } catch (e) {
      unwrapAxiosError(e);
    }
  }

  /**
   * 직접 제어 — fan-out 비의존 결정론적 제어.
   * type: 'dimming'(통로등) | 'warnLamp'(경광등) | 'parking'(만공차등)
   * value: 0=off, 1~100=on(밝기)
   */
  static async triggerControl(
    cctvNames: string[],
    type: 'dimming' | 'warnLamp' | 'parking',
    value: number,
    mode: 'auto' | 'manual' = 'auto',
  ): Promise<ControlResult> {
    try {
      const res = await apiClient.post<{
        success?: boolean;
        message?: string;
        requested?: number;
        captured?: number;
        errors?: string[];
      }>(API_ENDPOINTS.LIGHTS_SIMULATOR.CONTROL, { cctvNames, type, value, mode });
      checkEnvelope(res.data);
      return {
        requested: res.data.requested ?? cctvNames.length,
        captured: typeof res.data.captured === 'number' ? res.data.captured : null,
        errors: res.data.errors ?? [],
      };
    } catch (e) {
      unwrapAxiosError(e);
    }
  }
}
